import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import AppLayout from '../../layouts/AppLayout.jsx';
import ApiClient from '../../utils/ApiClient.js';
import CopyableEmail from '../../components/CopyableEmail';
import ValueWithSkeleton from '../../components/ValueWithSkeleton/ValueWithSkeleton';
import { firstResolvedDisplayName } from '../../utils/DisplayValueUtils.js';

const APPROVER_SUFFIX = '-Approvers';

const extractGroupList = (value) => {
    if (!value) {
        return [];
    }
    if (Array.isArray(value)) {
        return value;
    }
    if (Array.isArray(value.content)) {
        return value.content;
    }
    if (Array.isArray(value.groups)) {
        return value.groups;
    }
    return [];
};

const extractMemberList = (value) => {
    if (!value) {
        return [];
    }
    if (Array.isArray(value)) {
        return value;
    }
    if (Array.isArray(value.content)) {
        return value.content;
    }
    if (Array.isArray(value.members)) {
        return value.members;
    }
    if (Array.isArray(value.users)) {
        return value.users;
    }
    return [];
};

const normalizeGroup = (group, fallbackId = '') => {
    if (!group || typeof group !== 'object') {
        const fallback = String(fallbackId ?? '').trim();
        return {
            id: fallback,
            name: fallback || 'Unknown group',
        };
    }

    const id = String(group.id ?? group.uuid ?? fallbackId ?? '').trim();
    const name = firstResolvedDisplayName(
        [group.name, group.displayName, id],
        'Unknown group'
    );

    return {
        id,
        name,
    };
};

const normalizeMembers = (members) => {
    const list = Array.isArray(members) ? members : [];
    const seen = new Set();

    return list
        .map((member, index) => {
            const fallbackId = `member-${index + 1}`;

            if (typeof member === 'string' || typeof member === 'number') {
                const value = String(member).trim();
                return {
                    id: value || fallbackId,
                    userLookupId: value,
                    displayName: value || 'Unknown member',
                    email: '',
                };
            }

            if (!member || typeof member !== 'object') {
                return null;
            }

            const id = String(
                member.id ??
                member.uuid ??
                member.userId ??
                member.username ??
                member.email ??
                ''
            ).trim();
            const displayName = firstResolvedDisplayName(
                [
                    member.name,
                    `${member.firstName || member.given_name || ''} ${member.lastName || member.family_name || ''}`.trim(),
                    member.displayName,
                    member.preferred_username,
                    member.username,
                    member.email,
                    id,
                ],
                'Unknown member'
            );
            const email = String(
                member.email ??
                member.mail ??
                member.userPrincipalName ??
                ''
            ).trim();

            return {
                id: id || fallbackId,
                userLookupId: id,
                displayName,
                email,
            };
        })
        .filter(Boolean)
        .filter((member) => {
            const dedupeKey = `${member.id}|${member.displayName}|${member.email}`;
            if (seen.has(dedupeKey)) {
                return false;
            }
            seen.add(dedupeKey);
            return true;
        });
};

const resolvePrefixFromApproverGroupName = (groupName) => {
    const normalizedGroupName = String(groupName ?? '').trim();
    if (!normalizedGroupName) {
        return '';
    }

    if (normalizedGroupName.endsWith(APPROVER_SUFFIX)) {
        const baseName = normalizedGroupName
            .slice(0, normalizedGroupName.length - APPROVER_SUFFIX.length)
            .trim();

        if (baseName) {
            return baseName;
        }
    }

    return normalizedGroupName.split('-')[0]?.trim() ?? '';
};

const resolveInitials = (name) => {
    const segments = String(name ?? '')
        .trim()
        .split(/\s+/)
        .filter(Boolean);
    if (segments.length === 0) {
        return '?';
    }

    return segments
        .slice(0, 2)
        .map((segment) => segment.charAt(0).toUpperCase())
        .join('');
};

const resolveDisclosureId = (groupId) => {
    const normalizedGroupId = String(groupId ?? '')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9_-]/g, '-')
        .replace(/-+/g, '-');
    return normalizedGroupId || 'group';
};

const ApprovalGroupDetailsPage = ({ authDetails, onSignOut }) => {
    const { uuid } = useParams();
    const [rootGroup, setRootGroup] = useState(null);
    const [managedGroups, setManagedGroups] = useState([]);
    const [managedGroupMembersLookup, setManagedGroupMembersLookup] = useState(new Map());
    const [managedGroupMemberPhotoLookup, setManagedGroupMemberPhotoLookup] = useState(new Map());
    const [rootGroupPhotoUrl, setRootGroupPhotoUrl] = useState('');
    const [expandedManagedGroupsLookup, setExpandedManagedGroupsLookup] = useState({});
    const [uniformMemberCardHeight, setUniformMemberCardHeight] = useState(0);
    const [isLoadingGroupDetails, setIsLoadingGroupDetails] = useState(true);
    const [isLoadingMembers, setIsLoadingMembers] = useState(false);
    const [groupDetailsError, setGroupDetailsError] = useState('');
    const memberCardContainerRef = useRef(null);
    const currentUserEmail = String(authDetails?.identity?.tokenParsed?.email ?? '').trim();

    const managedGroupPrefix = useMemo(() => {
        return resolvePrefixFromApproverGroupName(rootGroup?.name ?? '');
    }, [rootGroup]);

    useEffect(() => {
        let isMounted = true;
        const normalizedUuid = String(uuid ?? '').trim();

        if (!normalizedUuid) {
            setRootGroup(null);
            setManagedGroups([]);
            setManagedGroupMembersLookup(new Map());
            setManagedGroupMemberPhotoLookup(new Map());
            setRootGroupPhotoUrl('');
            setGroupDetailsError('Approval group identifier is missing.');
            setIsLoadingGroupDetails(false);
            return () => {
                isMounted = false;
            };
        }

        const fetchGroupDetails = async () => {
            setIsLoadingGroupDetails(true);
            setGroupDetailsError('');
            setManagedGroupMembersLookup(new Map());
            setManagedGroupMemberPhotoLookup(new Map());
            setRootGroupPhotoUrl('');

            try {
                const [groupResponse, groupsResponse] = await Promise.all([
                    ApiClient.get(`/groups/${encodeURIComponent(normalizedUuid)}`),
                    ApiClient.get('/groups'),
                ]);

                if (groupResponse.status !== 200 || groupsResponse.status !== 200) {
                    throw new Error('Unable to retrieve group details');
                }

                const resolvedRootGroup = normalizeGroup(groupResponse.data ?? {}, normalizedUuid);
                const groupPrefix = resolvePrefixFromApproverGroupName(resolvedRootGroup.name);
                const normalizedPrefix = String(groupPrefix).toLowerCase();
                const allGroups = extractGroupList(groupsResponse.data)
                    .map((group) => normalizeGroup(group))
                    .filter((group) => Boolean(group.id && group.name));
                const relatedGroups = allGroups
                    .filter((group) => {
                        if (!groupPrefix) {
                            return false;
                        }

                        const normalizedGroupName = String(group.name).toLowerCase();
                        return normalizedGroupName.startsWith(`${normalizedPrefix}-`);
                    })
                    .sort((left, right) => left.name.localeCompare(right.name, undefined, {
                        sensitivity: 'base',
                        numeric: true,
                    }));

                const relatedGroupsWithApprover = [
                    normalizeGroup(resolvedRootGroup, normalizedUuid),
                    ...relatedGroups,
                ].filter((group, index, list) => {
                    const groupId = String(group.id).trim();
                    return Boolean(groupId) && list.findIndex((item) => item.id === groupId) === index;
                });

                if (isMounted) {
                    setRootGroup(resolvedRootGroup);
                    setManagedGroups(relatedGroupsWithApprover);
                    setManagedGroupMembersLookup(new Map());
                    setManagedGroupMemberPhotoLookup(new Map());
                }
            } catch (error) {
                if (isMounted) {
                    setRootGroup(null);
                    setManagedGroups([]);
                    setManagedGroupMembersLookup(new Map());
                    setManagedGroupMemberPhotoLookup(new Map());
                    setRootGroupPhotoUrl('');
                    setGroupDetailsError('We could not load this approval group detail page.');
                }
            } finally {
                if (isMounted) {
                    setIsLoadingGroupDetails(false);
                }
            }
        };

        fetchGroupDetails();

        return () => {
            isMounted = false;
        };
    }, [uuid]);

    useEffect(() => {
        let isMounted = true;
        const relatedGroups = Array.isArray(managedGroups) ? managedGroups : [];

        if (relatedGroups.length === 0) {
            setManagedGroupMembersLookup(new Map());
            setIsLoadingMembers(false);
            return () => {
                isMounted = false;
            };
        }

        const fetchManagedGroupMembers = async () => {
            setIsLoadingMembers(true);
            try {
                const responses = await Promise.all(
                    relatedGroups.map(async (group) => {
                        try {
                            const response = await ApiClient.get(
                                `/groups/${encodeURIComponent(group.id)}/members`
                            );
                            if (response.status !== 200) {
                                throw new Error(`API responded with ${response.status}`);
                            }

                            return {
                                groupId: group.id,
                                members: normalizeMembers(extractMemberList(response.data)),
                            };
                        } catch (error) {
                            return {
                                groupId: group.id,
                                members: [],
                            };
                        }
                    })
                );

                const nextLookup = new Map();
                responses.forEach((response) => {
                    if (response?.groupId) {
                        nextLookup.set(response.groupId, response.members ?? []);
                    }
                });

                if (isMounted) {
                    setManagedGroupMembersLookup(nextLookup);
                }
            } finally {
                if (isMounted) {
                    setIsLoadingMembers(false);
                }
            }
        };

        fetchManagedGroupMembers();

        return () => {
            isMounted = false;
        };
    }, [managedGroups]);

    useEffect(() => {
        const relatedGroups = Array.isArray(managedGroups) ? managedGroups : [];
        if (relatedGroups.length === 0) {
            setExpandedManagedGroupsLookup({});
            return;
        }

        setExpandedManagedGroupsLookup((currentLookup) => {
            const nextLookup = {};
            relatedGroups.forEach((group) => {
                const groupId = String(group?.id ?? '').trim();
                if (!groupId) {
                    return;
                }
                nextLookup[groupId] = currentLookup[groupId] ?? true;
            });
            return nextLookup;
        });
    }, [managedGroups]);

    useEffect(() => {
        let isMounted = true;
        let objectUrls = [];
        const memberPhotoLookupIds = Array.from(
            new Set(
                Array.from(managedGroupMembersLookup.values())
                    .flatMap((members) => (Array.isArray(members) ? members : []))
                    .map((member) => String(member?.userLookupId ?? '').trim())
                    .filter(Boolean)
            )
        );

        if (memberPhotoLookupIds.length === 0) {
            setManagedGroupMemberPhotoLookup(new Map());
            return () => {
                isMounted = false;
            };
        }

        const fetchMemberPhotos = async () => {
            try {
                const responses = await Promise.all(
                    memberPhotoLookupIds.map(async (memberPhotoLookupId) => {
                        try {
                            const response = await ApiClient.get(
                                `/users/${encodeURIComponent(memberPhotoLookupId)}/picture`,
                                {
                                    responseType: 'blob',
                                }
                            );

                            if (response.status !== 200) {
                                throw new Error(`API responded with ${response.status}`);
                            }

                            const contentType = response.headers?.['content-type'] ?? '';
                            if (contentType && !contentType.startsWith('image/')) {
                                throw new Error(`Unexpected content type: ${contentType}`);
                            }

                            if (typeof URL?.createObjectURL !== 'function') {
                                throw new Error('Object URL API is unavailable');
                            }

                            const objectUrl = URL.createObjectURL(response.data);
                            objectUrls = [...objectUrls, objectUrl];

                            return {
                                memberPhotoLookupId,
                                objectUrl,
                            };
                        } catch (error) {
                            return null;
                        }
                    })
                );

                const lookup = new Map();
                responses.filter(Boolean).forEach((response) => {
                    if (response?.memberPhotoLookupId && response?.objectUrl) {
                        lookup.set(response.memberPhotoLookupId, response.objectUrl);
                    }
                });

                if (isMounted) {
                    setManagedGroupMemberPhotoLookup(lookup);
                }
            } catch (error) {
                if (isMounted) {
                    setManagedGroupMemberPhotoLookup(new Map());
                }
            }
        };

        fetchMemberPhotos();

        return () => {
            isMounted = false;
            objectUrls.forEach((objectUrl) => {
                if (typeof URL?.revokeObjectURL === 'function') {
                    URL.revokeObjectURL(objectUrl);
                }
            });
            objectUrls = [];
        };
    }, [managedGroupMembersLookup]);

    useEffect(() => {
        let isMounted = true;
        let objectUrl = '';
        const rootGroupId = String(rootGroup?.id ?? '').trim();

        if (!rootGroupId) {
            setRootGroupPhotoUrl('');
            return () => {
                isMounted = false;
            };
        }

        const fetchRootGroupPhoto = async () => {
            if (isMounted) {
                setRootGroupPhotoUrl('');
            }
            try {
                const response = await ApiClient.get(
                    `/groups/${encodeURIComponent(rootGroupId)}/picture`,
                    {
                        responseType: 'blob',
                    }
                );

                if (response.status !== 200) {
                    throw new Error(`API responded with ${response.status}`);
                }

                const contentType = response.headers?.['content-type'] ?? '';
                if (contentType && !contentType.startsWith('image/')) {
                    throw new Error(`Unexpected content type: ${contentType}`);
                }

                const imageBlob = response.data;
                if (!(imageBlob instanceof Blob) || imageBlob.size === 0) {
                    throw new Error('Root group picture payload was not an image blob');
                }

                if (typeof URL?.createObjectURL !== 'function') {
                    throw new Error('Object URL API is unavailable');
                }

                objectUrl = URL.createObjectURL(imageBlob);
                if (isMounted) {
                    setRootGroupPhotoUrl(objectUrl);
                }
            } catch (error) {
                if (isMounted) {
                    setRootGroupPhotoUrl('');
                }
            }
        };

        fetchRootGroupPhoto();

        return () => {
            isMounted = false;
            if (objectUrl && typeof URL?.revokeObjectURL === 'function') {
                URL.revokeObjectURL(objectUrl);
            }
            objectUrl = '';
        };
    }, [rootGroup]);

    useEffect(() => {
        let isMounted = true;

        const recalculateMemberCardHeight = () => {
            const container = memberCardContainerRef.current;
            if (!container || !isMounted) {
                return;
            }

            const cardNodes = Array.from(
                container.querySelectorAll('.approval-group-prototype-card__member-card')
            ).filter((node) => !node.closest('[hidden]'));

            if (cardNodes.length === 0) {
                setUniformMemberCardHeight((current) => (current === 0 ? current : 0));
                return;
            }

            const tallestCardHeight = Math.ceil(
                cardNodes.reduce((maxHeight, node) => {
                    const height = node.getBoundingClientRect().height;
                    return Math.max(maxHeight, height);
                }, 0)
            );

            setUniformMemberCardHeight((current) =>
                current === tallestCardHeight ? current : tallestCardHeight
            );
        };

        const animationFrameId = window.requestAnimationFrame(recalculateMemberCardHeight);
        window.addEventListener('resize', recalculateMemberCardHeight);

        return () => {
            isMounted = false;
            window.cancelAnimationFrame(animationFrameId);
            window.removeEventListener('resize', recalculateMemberCardHeight);
        };
    }, [
        managedGroups,
        managedGroupMembersLookup,
        managedGroupMemberPhotoLookup,
        expandedManagedGroupsLookup,
        isLoadingMembers,
    ]);

    const pageHeading = rootGroup?.name
        ? `Entitlement View - ${rootGroup.name}`
        : 'Entitlement View';
    const totalManagedMembers = managedGroups.reduce((total, group) => {
        const groupId = String(group?.id ?? '').trim();
        const members = managedGroupMembersLookup.get(groupId) ?? [];
        return total + members.length;
    }, 0);
    const rootGroupId = String(rootGroup?.id ?? '').trim();
    const toggleManagedGroup = (groupId) => {
        const normalizedGroupId = String(groupId ?? '').trim();
        if (!normalizedGroupId) {
            return;
        }
        setExpandedManagedGroupsLookup((currentLookup) => ({
            ...currentLookup,
            [normalizedGroupId]: !(currentLookup[normalizedGroupId] ?? true),
        }));
    };

    return (
        <AppLayout sidebar authDetails={authDetails} onSignOut={onSignOut}>
            <div className="grid-container">
                <div className="grid-row grid-gap">
                    <div className="tablet:grid-col-12">
                        <div className="approval-group-prototype-card" ref={memberCardContainerRef}>
                            <div className="approval-group-prototype-card__body">
                                <h1 className="approval-group-prototype-card__page-heading">{pageHeading}</h1>
                                <p className="usa-prose approval-group-prototype-card__back-link">
                                    <Link to="/approvals">Back to approvals</Link>
                                </p>
                                {groupDetailsError ? (
                                    <div className="usa-alert usa-alert--error" role="alert">
                                        <div className="usa-alert__body">
                                            <p className="usa-alert__text">{groupDetailsError}</p>
                                        </div>
                                    </div>
                                ) : null}
                                {isLoadingGroupDetails ? (
                                    <div aria-hidden="true">
                                        <p className="approval-group-prototype-card__prefix">
                                            <ValueWithSkeleton loading type="sentence" width="24ch" />
                                        </p>
                                        <section className="approval-group-prototype-card__root">
                                            <div className="approval-group-prototype-card__root-icon" aria-hidden="true">
                                                <span className="approval-group-prototype-card__root-badge" />
                                            </div>
                                            <div className="approval-group-prototype-card__root-copy">
                                                <span className="approval-group-prototype-card__root-label">
                                                    <ValueWithSkeleton loading width="10ch" />
                                                </span>
                                                <h2 className="approval-group-prototype-card__root-name">
                                                    <ValueWithSkeleton loading type="heading" width="18ch" />
                                                </h2>
                                                <div className="approval-group-prototype-card__root-stats">
                                                    <span className="approval-group-prototype-card__stat-chip">
                                                        <ValueWithSkeleton loading type="chip" width="8ch" />
                                                    </span>
                                                    <span className="approval-group-prototype-card__stat-chip">
                                                        <ValueWithSkeleton loading type="chip" width="8ch" />
                                                    </span>
                                                </div>
                                            </div>
                                        </section>
                                        <section className="approval-group-prototype-card__groups">
                                            <ul className="approval-group-prototype-card__group-list">
                                                {Array.from({ length: 2 }).map((_, index) => (
                                                    <li
                                                        key={`approval-group-loading-${index}`}
                                                        className="approval-group-prototype-card__group-item"
                                                    >
                                                        <div className="approval-group-prototype-card__group-toggle">
                                                            <span className="approval-group-prototype-card__group-icon" aria-hidden="true">
                                                                G
                                                            </span>
                                                            <span className="approval-group-prototype-card__group-copy">
                                                                <span className="approval-group-prototype-card__group-name">
                                                                    <ValueWithSkeleton loading type="sentence" width="16ch" />
                                                                </span>
                                                                <span className="approval-group-prototype-card__group-role">
                                                                    <ValueWithSkeleton loading width="12ch" />
                                                                </span>
                                                            </span>
                                                        </div>
                                                    </li>
                                                ))}
                                            </ul>
                                        </section>
                                    </div>
                                ) : rootGroup ? (
                                    <>
                                        <p className="approval-group-prototype-card__prefix">
                                            Showing groups with prefix{' '}
                                            <strong>{managedGroupPrefix || 'N/A'}</strong>
                                        </p>

                                        <section
                                            className="approval-group-prototype-card__root"
                                            aria-label="Approval group organization chart"
                                        >
                                            <div className="approval-group-prototype-card__root-icon" aria-hidden="true">
                                                {rootGroupPhotoUrl ? (
                                                    <img
                                                        className="approval-group-prototype-card__root-photo"
                                                        src={rootGroupPhotoUrl}
                                                        alt=""
                                                    />
                                                ) : (
                                                    <span className="approval-group-prototype-card__root-badge" />
                                                )}
                                            </div>
                                            <div className="approval-group-prototype-card__root-copy">
                                                <span className="approval-group-prototype-card__root-label">
                                                    Root Approver
                                                </span>
                                                <h2 className="approval-group-prototype-card__root-name">
                                                    {rootGroup.name}
                                                </h2>
                                                <div className="approval-group-prototype-card__root-stats">
                                                    <span className="approval-group-prototype-card__stat-chip">
                                                        {managedGroups.length}{' '}
                                                        {managedGroups.length === 1 ? 'Group' : 'Groups'}
                                                    </span>
                                                    <span className="approval-group-prototype-card__stat-chip">
                                                        {totalManagedMembers}{' '}
                                                        {totalManagedMembers === 1 ? 'User' : 'Users'}
                                                    </span>
                                                </div>
                                            </div>
                                        </section>

                                        <div
                                            className="approval-group-prototype-card__connector"
                                            aria-hidden="true"
                                        />

                                        <section className="approval-group-prototype-card__groups">
                                            <div className="approval-group-prototype-card__groups-header">
                                                <h3 className="approval-group-prototype-card__groups-heading">
                                                    Member Groups
                                                </h3>
                                                <p className="approval-group-prototype-card__groups-description">
                                                    Groups and users within the {rootGroup.name} hierarchy
                                                </p>
                                            </div>

                                            {managedGroups.length > 0 ? (
                                                <ul className="approval-group-prototype-card__group-list">
                                                    {managedGroups.map((group) => {
                                                        const groupId = String(group.id ?? '').trim();
                                                        const members =
                                                            managedGroupMembersLookup.get(groupId) ?? [];
                                                        const disclosureId = `approval-group-members-${resolveDisclosureId(groupId)}`;
                                                        const isExpanded =
                                                            expandedManagedGroupsLookup[groupId] ?? true;
                                                        const isRootManagedGroup = groupId === rootGroupId;
                                                        const resolvePhotoUrl = (member) => {
                                                            const lookupId = String(
                                                                member?.userLookupId ?? ''
                                                            ).trim();
                                                            if (!lookupId) {
                                                                return '';
                                                            }
                                                            return (
                                                                managedGroupMemberPhotoLookup.get(lookupId) ?? ''
                                                            );
                                                        };

                                                        return (
                                                            <li
                                                                className="approval-group-prototype-card__group-item"
                                                                key={groupId || group.name}
                                                            >
                                                                <button
                                                                    type="button"
                                                                    className="approval-group-prototype-card__group-toggle"
                                                                    onClick={() => toggleManagedGroup(groupId)}
                                                                    aria-expanded={isExpanded}
                                                                    aria-controls={disclosureId}
                                                                >
                                                                    <span
                                                                        className="approval-group-prototype-card__group-icon"
                                                                        aria-hidden="true"
                                                                    >
                                                                        {isRootManagedGroup ? 'A' : 'G'}
                                                                    </span>
                                                                    <span className="approval-group-prototype-card__group-copy">
                                                                        <span
                                                                            className="approval-group-prototype-card__group-name"
                                                                            role="heading"
                                                                            aria-level={3}
                                                                        >
                                                                            {group.name}
                                                                        </span>
                                                                        <span className="approval-group-prototype-card__group-role">
                                                                            {isRootManagedGroup
                                                                                ? 'Approver group members'
                                                                                : 'Managed group'}
                                                                        </span>
                                                                    </span>
                                                                    <span className="approval-group-prototype-card__group-meta">
                                                                        <span className="approval-group-prototype-card__group-count">
                                                                            {members.length} member
                                                                            {members.length === 1 ? '' : 's'}
                                                                        </span>
                                                                        <span
                                                                            className="approval-group-prototype-card__group-chevron"
                                                                            aria-hidden="true"
                                                                        >
                                                                            {isExpanded ? '▴' : '▾'}
                                                                        </span>
                                                                    </span>
                                                                </button>

                                                                <div
                                                                    className="approval-group-prototype-card__members"
                                                                    id={disclosureId}
                                                                    hidden={!isExpanded}
                                                                >
                                                                    {isLoadingMembers ? (
                                                                        <div
                                                                            className="approval-group-prototype-card__loading"
                                                                            aria-hidden="true"
                                                                        >
                                                                            {Array.from({ length: 2 }).map((_, index) => (
                                                                                <p key={`approval-group-member-loading-${groupId}-${index}`}>
                                                                                    <ValueWithSkeleton loading type="sentence" width="18ch" />
                                                                                </p>
                                                                            ))}
                                                                        </div>
                                                                    ) : members.length > 0 ? (
                                                                        <ul className="approval-group-prototype-card__member-list">
                                                                            {members.map((member) => {
                                                                                const memberPhotoUrl =
                                                                                    resolvePhotoUrl(member);
                                                                                return (
                                                                                    <li
                                                                                        className="approval-group-prototype-card__member-item"
                                                                                        key={`${groupId}-${member.id}`}
                                                                                    >
                                                                                        <div
                                                                                            className="approval-group-prototype-card__member-card"
                                                                                            style={
                                                                                                uniformMemberCardHeight
                                                                                                    ? { minHeight: `${uniformMemberCardHeight}px` }
                                                                                                    : undefined
                                                                                            }
                                                                                        >
                                                                                            <div className="approval-group-prototype-card__member-avatar-wrap">
                                                                                                {memberPhotoUrl ? (
                                                                                                    <img
                                                                                                        className="approval-group-prototype-card__member-avatar"
                                                                                                        src={memberPhotoUrl}
                                                                                                        alt={member.displayName}
                                                                                                    />
                                                                                                ) : (
                                                                                                    <div
                                                                                                        className="approval-group-prototype-card__member-avatar approval-group-prototype-card__member-avatar--placeholder"
                                                                                                        aria-hidden="true"
                                                                                                    >
                                                                                                        {resolveInitials(member.displayName)}
                                                                                                    </div>
                                                                                                )}
                                                                                            </div>
                                                                                            <div className="approval-group-prototype-card__member-copy">
                                                                                                <h4 className="approval-group-prototype-card__member-name">
                                                                                                    {member.displayName}
                                                                                                </h4>
                                                                                                <div className="approval-group-prototype-card__member-email">
                                                                                                    <CopyableEmail
                                                                                                        email={member.email}
                                                                                                        currentUserEmail={currentUserEmail}
                                                                                                        fallback="No email listed"
                                                                                                    />
                                                                                                </div>
                                                                                            </div>
                                                                                        </div>
                                                                                    </li>
                                                                                );
                                                                            })}
                                                                        </ul>
                                                                    ) : null}
                                                                </div>
                                                            </li>
                                                        );
                                                    })}
                                                </ul>
                                            ) : (
                                                <p className="approval-group-prototype-card__empty">
                                                    No managed groups found
                                                </p>
                                            )}
                                        </section>
                                    </>
                                ) : (
                                    <p className="usa-prose">Approval group details are unavailable.</p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </AppLayout>
    );
};

export default ApprovalGroupDetailsPage;
